import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ordersAPI } from '../../services/api';

const initialState = {
  cart: { items: [], total: 0, itemCount: 0 },
  orders: [],
  currentOrder: null,
  pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  isLoading: false,
  error: null,
  checkoutStep: 1,
  pendingOperations: 0,
};

export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.getCart();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async ({ productId, quantity }, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.addToCart({ productId, quantity });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async ({ itemId, quantity }, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.updateCartItem(itemId, { quantity });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (itemId, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.removeFromCart(itemId);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.clearCart();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createOrder = createAsyncThunk(
  'cart/createOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.create(orderData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchOrders = createAsyncThunk(
  'cart/fetchOrders',
  async (params, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.getAll(params);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'cart/fetchOrderById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.getById(id);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCheckoutStep: (state, action) => {
      state.checkoutStep = action.payload;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.pendingOperations += 1;
        state.isLoading = true;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        state.cart = action.payload;
        state.error = null;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        state.error = action.payload;
      })
      .addCase(addToCart.pending, (state) => { state.pendingOperations += 1; })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        if (action.payload) state.cart = action.payload;
        state.error = null;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        state.error = action.payload;
      })
      .addCase(updateCartItem.pending, (state) => { state.pendingOperations += 1; })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        if (action.payload) state.cart = action.payload;
        state.error = null;
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        state.error = action.payload;
      })
      .addCase(removeFromCart.pending, (state) => { state.pendingOperations += 1; })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        if (action.payload) state.cart = action.payload;
        state.error = null;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        state.error = action.payload;
      })
      .addCase(clearCart.pending, (state) => { state.pendingOperations += 1; })
      .addCase(clearCart.fulfilled, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        if (action.payload) state.cart = action.payload;
        state.error = null;
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.isLoading = state.pendingOperations > 0;
        state.error = action.payload;
      })
      .addCase(createOrder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload.order;
        state.checkoutStep = 3;
        state.error = null;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.currentOrder = action.payload;
      });
  },
});

export const { setCheckoutStep, clearCurrentOrder, clearError } = cartSlice.actions;
export default cartSlice.reducer;
